import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

export type EventoCalendario = {
    id: string
    titulo: string
    /** `YYYY-MM-DD`, inclusivo. */
    inicio: string
    /** `YYYY-MM-DD`, inclusivo; `null` = indeterminado. */
    fim: string | null
    /** Cor de fundo; sem ela usa a cor primária. */
    cor?: string
    corTexto?: string
    className?: string
    /** Tooltip; padrão é o título. */
    dica?: string
}

/** 6 semanas fixas a partir do domingo da semana do dia 1 — altura estável entre meses. */
export function diasDaGrade(competencia: string): Dayjs[] {
    const primeiro = dayjs.utc(`${competencia}-01`)
    const inicio = primeiro.subtract(primeiro.day(), 'day')
    return Array.from({ length: 42 }, (_, i) => inicio.add(i, 'day'))
}

/** Eventos que cobrem o dia, na ordem recebida. */
export function eventosDoDia(eventos: EventoCalendario[], dia: Dayjs) {
    const data = dia.format('YYYY-MM-DD')
    return eventos.filter(
        e => e.inicio <= data && (e.fim === null || data <= e.fim),
    )
}

/**
 * Pedaço da barra que o dia desenha. Cada célula pinta só o seu dia; os
 * pedaços sem canto arredondado encostam nos vizinhos e formam a barra
 * contínua. A barra abre quando o dia anterior não mostra o mesmo evento ou
 * é domingo, e fecha no simétrico.
 */
export function pedacoDoDia(
    eventos: EventoCalendario[],
    dia: Dayjs,
    competencia: string,
) {
    const doDia = eventosDoDia(eventos, dia)
    const evento = doDia[0]
    if (!evento) return null

    const anterior = eventosDoDia(eventos, dia.subtract(1, 'day'))[0],
        seguinte = eventosDoDia(eventos, dia.add(1, 'day'))[0]

    const abre = dia.day() === 0 || anterior?.id !== evento.id
    const isNoMes = (data: Dayjs) => data.format('YYYY-MM') === competencia

    return {
        evento,
        abre,
        fecha: dia.day() === 6 || seguinte?.id !== evento.id,
        ocultos: doDia.length - 1,
        // dia fora do mês é esmaecido e o texto herdaria a opacidade: o título
        // nasce no 1º dia do mês em que a barra aparece
        mostraTitulo:
            isNoMes(dia) && (abre || !isNoMes(dia.subtract(1, 'day'))),
        extensao: extensaoDoTitulo(eventos, evento, dia, isNoMes),
    }
}

/**
 * Quantos dias, a partir deste, a barra segue com o mesmo evento visível
 * dentro do mês — no máximo até o sábado. O título usa essa largura para
 * transbordar sobre os pedaços vizinhos em vez de cortar no primeiro dia.
 */
function extensaoDoTitulo(
    eventos: EventoCalendario[],
    evento: EventoCalendario,
    dia: Dayjs,
    isNoMes: (data: Dayjs) => boolean,
) {
    let extensao = 1
    for (let atual = dia; atual.day() < 6; atual = atual.add(1, 'day')) {
        const seguinte = atual.add(1, 'day')
        if (
            !isNoMes(seguinte) ||
            eventosDoDia(eventos, seguinte)[0]?.id !== evento.id
        )
            break
        extensao++
    }
    return extensao
}

/** Se o dia da grade (UTC) é o dia de hoje na data local do usuário. */
export function isHoje(dia: Dayjs) {
    return dia.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD')
}

/**
 * Período de um clique e arraste na grade, com as pontas em ordem — arrastar
 * para trás vale o mesmo. Soltar no dia em que apertou é clique simples:
 * sem fim.
 */
export function periodoArrastado(apertou: Dayjs, soltou: Dayjs) {
    const [inicio, fim] = apertou.isAfter(soltou, 'day')
        ? [soltou, apertou]
        : [apertou, soltou]
    return { inicio, fim: inicio.isSame(fim, 'day') ? null : fim }
}
